import { Injectable, inject } from '@angular/core';

import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { popupManualClose } from '@frontend/sports/modal/feature';
import { ModelPopupTypes } from '@frontend/sports/navigation/core/feature/url-helper';
import { PopupManager } from '@frontend/sports/popup/feature';
import { trackingConstants } from '@frontend/sports/tracking/feature';
import { TimerService } from '@frontend/vanilla/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { concatMap, delay, filter, map, of, switchMap, take, tap } from 'rxjs';

import { betslipSelector, selectIsLinearBetslip } from '../../../base/store/selectors';
import { BetslipBetBuilderPick } from '../../../core/picks/betslip-bet-builder-pick';
import { BaseBetslipGroupPick } from '../../../core/picks/betslip-group-pick';
import { BetslipUnknownPick } from '../../../core/picks/betslip-unknown-pick';
import { GroupPickId } from '../../../core/picks/pick-id';
import { BetslipPlaceBetActions } from '../../betplacement/actions';
import { betslipPreBetStatusSelector } from '../../betplacement/selectors';
import BetslipPickActions from '../../picks/actions';
import BetslipPickFactory from '../../picks/factory/betslip-pick.factory';
import { addGroup, removePicksFromGroup, replaceGroup, requestAddPickToGroup } from '../../picks/grouped-picks.actions';
import { ofTypeReplaceExistingPick } from '../../picks/helpers';
import { QuickBetActions } from '../../quick-bet/quick-bet-actions';
import { settingsOddsAcceptanceSelector } from '../../settings/selectors';
import { betslipTypesBaseSelector } from '../../types/base/selectors';
import BetslipErrorsActions from '../../validation/actions';
import { BetslipTrackingConstants } from '../models';
import { BetslipBaseTrackingService } from '../services/betslip-base-tracking.service';
import { BetslipLaunchDarklyTrackingService } from '../services/betslip-launch-darkly-tracking.service';

@Injectable()
export default abstract class BetslipBaseTrackingEffects {
    protected timerService = inject(TimerService);
    protected popupManagerService = inject(PopupManager);

    constructor(
        protected actions$: Actions,
        protected store: Store,
        protected betslipTrackingService: BetslipBaseTrackingService,
        protected pickFactory: BetslipPickFactory,
        protected betslipLaunchDarklyTrackingService: BetslipLaunchDarklyTrackingService,
    ) {}

    handleAggregatedAddPickTrackingEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofTypeReplaceExistingPick(),
                delay(5),
                filter((actions) => !!actions.newPickAdded && !(<BetslipBetBuilderPick>actions.newPick).sportcastOptions?.length),
                concatLatestFrom(() => this.store.select(betslipSelector)),
                tap(([actions, state]) => this.betslipTrackingService.trackAddRemovePick(trackingConstants.EVENT_CART_ADD, actions.newPick, state)),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleAddPickToGroupEffect$ = createEffect(
        () => {
            const replaceGroup$ = this.actions$.pipe(ofType(replaceGroup));

            return this.actions$.pipe(
                ofType(requestAddPickToGroup),
                switchMap((action) => {
                    return replaceGroup$.pipe(
                        map((payload) => {
                            return {
                                lastAddedPick: payload.newPick.getLegs().find((p) => p.id.isEqual(action.pickId)),
                                groupId: payload.newPick.groupInfo.groupId,
                            };
                        }),
                        take(1),
                    );
                }),
                concatLatestFrom(() => this.store.select(betslipSelector)),
                tap(([{ lastAddedPick, groupId }, state]) => {
                    if (lastAddedPick && !BetslipUnknownPick.isPick(lastAddedPick)) {
                        const copiedPick = lastAddedPick.copy();
                        this.betslipTrackingService.trackAddRemovePick(trackingConstants.EVENT_CART_ADD, copiedPick, state, undefined, groupId);
                    }
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleAddEntainUIPickTrackingEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipPickActions.requestAddEntainUiBetBuilderPick),
                concatLatestFrom(() => this.store.select(betslipSelector)),
                tap(([action, state]) => {
                    let betBuilderPick = state.picks.pickList.find(
                        (p) => BetslipBetBuilderPick.isEntainUiBetBuilderPick(p) && p.eventId === action.eventData.event.id,
                    ) as BetslipBetBuilderPick | undefined;

                    if (!betBuilderPick) {
                        const sportcastOptions = [
                            {
                                longId: action.longId[0],
                                name: action.market.name,
                            },
                        ];
                        betBuilderPick = this.pickFactory.createEntainUiBetBuilderPick(
                            action.pickId,
                            action.eventData,
                            action.eventModel,
                            action.market,
                            action.tracking,
                            sportcastOptions,
                        )!;
                    }

                    this.betslipTrackingService.trackAddRemovePick(
                        trackingConstants.EVENT_CART_ADD,
                        betBuilderPick,
                        state,
                        action.pickId.optionMarketId.toString(),
                    );
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleAddGroupPickTrackingEffect$ = createEffect(
        () => {
            const addReplaceGroup$ = this.actions$.pipe(ofType(addGroup, replaceGroup));

            return this.actions$.pipe(
                ofType(BetslipPickActions.requestAddGroupPick),
                concatMap(() => {
                    return addReplaceGroup$.pipe(
                        map((payload) => {
                            return {
                                lastAddedPick: payload.newPick,
                            };
                        }),
                        take(1),
                    );
                }),
                concatLatestFrom(() => this.store.select(betslipSelector)),
                tap(([{ lastAddedPick }, state]) => {
                    const group = state.picks.pickList.find(
                        (pick) => BaseBetslipGroupPick.isPick(pick) && pick.groupInfo.groupId === lastAddedPick.groupInfo.groupId,
                    ) as BaseBetslipGroupPick;

                    const picksToAdd = group
                        ? lastAddedPick.getLegs().filter((leg) => !group.getLegs().some((pickLeg) => pickLeg.id.toString() === leg.id.toString()))
                        : lastAddedPick.getLegs();
                    for (const leg of picksToAdd) {
                        this.betslipTrackingService.trackAddRemovePick(
                            trackingConstants.EVENT_CART_ADD,
                            leg,
                            state,
                            undefined,
                            lastAddedPick.groupInfo.groupId,
                        );
                    }
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleRemovePickTrackingEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipPickActions.requestRemovePick),
                concatLatestFrom(() => this.store.select(betslipSelector)),
                tap(([action, state]) => {
                    const pick = state.picks.pickList.find((p) => p.id.isEqual(action.pickId));
                    if (pick && !BetslipUnknownPick.isPick(pick)) {
                        if (GroupPickId.isId(action.pickId) && BaseBetslipGroupPick.isPick(pick)) {
                            for (const leg of pick.getLegs()) {
                                this.betslipTrackingService.trackAddRemovePick(
                                    trackingConstants.EVENT_CART_REMOVE,
                                    leg,
                                    state,
                                    undefined,
                                    pick.groupInfo.groupId,
                                );
                            }
                        } else {
                            this.betslipTrackingService.trackAddRemovePick(trackingConstants.EVENT_CART_REMOVE, pick, state);
                        }
                    }
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleRemovePicksFromGroupTrackingEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(removePicksFromGroup),
                concatLatestFrom(() => this.store.select(betslipSelector)),
                tap(([{ pickIds, groupInfo }, state]) => {
                    const group = state.picks.pickList.find(
                        (pick) => BaseBetslipGroupPick.isPick(pick) && pick.groupInfo.groupId === groupInfo.groupId,
                    ) as BaseBetslipGroupPick;

                    if (group) {
                        const stringPickIds = pickIds.map((id) => id.toString());
                        const removedPicks = group.getLegs().filter((leg) => stringPickIds.includes(leg.id.toString()));

                        if (removedPicks) {
                            for (const leg of removedPicks) {
                                if (!BetslipUnknownPick.isPick(leg)) {
                                    this.betslipTrackingService.trackAddRemovePick(
                                        trackingConstants.EVENT_CART_REMOVE,
                                        leg,
                                        state,
                                        undefined,
                                        group.groupInfo.groupId,
                                    );
                                }
                            }
                        }
                    }
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleRemoveEntainUiPickTrackingEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipPickActions.requestRemoveEntainUiBetBuilderPick),
                concatLatestFrom(() => this.store.select(betslipSelector)),
                tap(([action, state]) => {
                    const existingBetBuilderPickFromEntainUi = state.picks.pickList.find(
                        (p) => BetslipBetBuilderPick.isPick(p) && p.eventId === action.eventId,
                    ) as BetslipBetBuilderPick | undefined;

                    if (existingBetBuilderPickFromEntainUi) {
                        this.betslipTrackingService.trackAddRemovePick(
                            trackingConstants.EVENT_CART_REMOVE,
                            existingBetBuilderPickFromEntainUi,
                            state,
                            action.pickId.optionMarketId.toString(),
                        );
                    }
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleMultiRemovePicksTrackingEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipPickActions.requestRemoveMultiplePicks),
                concatLatestFrom(() => this.store.select(betslipSelector)),
                tap(([action, state]) => {
                    this.timerService.scheduleIdleCallback(() => {
                        action.pickIds.forEach((pickId) => {
                            const pick = state.picks.pickList.find((p) => p.id.isEqual(pickId));
                            if (pick) {
                                this.betslipTrackingService.trackAddRemovePick(trackingConstants.EVENT_CART_REMOVE, pick, state);
                            }
                        });
                    });
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleFinishPlaceBetTrackingEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPlaceBetActions.trackBetPlacementResult, QuickBetActions.trackBetPlacementResult),
            concatLatestFrom(() => this.store.select(betslipPreBetStatusSelector)),
            switchMap(([action, bettingStatus]) => {
                this.betslipLaunchDarklyTrackingService.trackIsBetPlacementSucceededFromFavourites(action.placeBetResult);
                this.betslipTrackingService.trackBetPlacementResult({ ...action, bettingStatus });
                return of(BetslipPlaceBetActions.refreshBetStatus());
            }),
        );
    });

    handlePlaceBetErrorTrackingEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipErrorsActions.placeBetResponseErrors, QuickBetActions.placeBetResponseErrors),
                concatLatestFrom(() => this.store.select(settingsOddsAcceptanceSelector)),
                tap(([action, oddsAcceptance]) => {
                    this.betslipTrackingService.trackBetPlacementErrors(action.pickErrors, action.betslipErrors, oddsAcceptance);
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleBetslipClientErrorsTrackingEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipErrorsActions.trackBetslipClientErrors),
                concatLatestFrom(() => this.store.select(selectIsLinearBetslip)),
                filter(([, isLinear]) => !isLinear),
                tap(([action]) => {
                    this.betslipTrackingService.trackBetslipClientErrors(action.trackingData);
                }),

                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleManualClosePopup$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(popupManualClose),
                filter((_) => this.popupManagerService.currentPopup === ModelPopupTypes.Betslip),
                concatLatestFrom(() => this.store.select(betslipTypesBaseSelector)),
                map(([_, betslipTypesBase]) => {
                    return this.betslipTrackingService.trackBetslipCTAClose({
                        action: BetslipTrackingConstants.Click,
                        positionEvent: betslipTypesBase.currentSelectedType ?? '',
                    });
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );
}
